import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ErrorAlert, LoadingList } from '../components/work/page-states';
import { HealthLegend, RagDot, StageChip } from '../components/work/ServiceRequestBadges';
import ServiceRequestShell from '../components/work/ServiceRequestShell';
import { apiErrorMessage } from '../lib/api';
import { startOfDay } from '../lib/assigned-queue';
import { formatDateOnly } from '../lib/dates';
import {
  addMonths,
  buildMonthGrid,
  fetchServiceRequestTimeline,
  monthGridRange,
  parseTimelineCalendarQuery,
  SR_PANEL,
  timelineItemPath,
  timelineKindLabel,
  timelineKindTone,
  toDateOnly,
  toMonthKey,
  weekdayHeadings,
  type CalendarDay,
  type TimelineItem,
} from '../lib/service-request-views';
import { cn } from '../lib/utils';

/** Events shown inside a day cell before the rest collapse into a "more" button. */
const PER_DAY_VISIBLE = 3;

function KindBadge({ kind }: { kind: string }) {
  return (
    <span className={cn('inline-flex whitespace-nowrap rounded-md px-1.5 py-0.5 text-[11px] font-medium', timelineKindTone(kind))}>
      {timelineKindLabel(kind)}
    </span>
  );
}

function itemKey(item: TimelineItem): string {
  return `${item.work_item_id}-${item.kind}-${item.task_id ?? ''}-${item.date}`;
}

function itemTitle(item: TimelineItem): string {
  return item.task_title ?? item.title;
}

/** One event inside a day cell: a colour-coded link straight to the task or request. */
function DayEvent({ item }: { item: TimelineItem }) {
  return (
    <Link
      to={timelineItemPath(item)}
      title={`${timelineKindLabel(item.kind)} · ${item.task_key ?? item.work_item_key} · ${itemTitle(item)}${item.customer ? ` · ${item.customer}` : ''}`}
      className={cn(
        'block truncate rounded px-1 py-0.5 text-[11px] leading-tight transition-opacity hover:opacity-80 hover:no-underline',
        timelineKindTone(item.kind),
        item.task_completed && 'line-through opacity-60',
      )}
    >
      {itemTitle(item)}
    </Link>
  );
}

function DayCell({
  day,
  today,
  month,
  selected,
  onSelect,
}: {
  day: CalendarDay;
  today: string;
  month: Date;
  selected: boolean;
  onSelect: (date: string) => void;
}) {
  const isToday = day.date === today;
  const hidden = Math.max(0, day.items.length - PER_DAY_VISIBLE);
  const dayNumber = Number(day.date.slice(8, 10));
  return (
    <div
      className={cn(
        'flex min-h-24 flex-col gap-1 border-b border-r p-1.5 last:border-r-0',
        !day.inMonth && 'bg-muted/30',
        selected && 'bg-accent/50 ring-1 ring-inset ring-primary/40',
      )}
      aria-label={formatDateOnly(day.date) ?? day.date}
      aria-current={isToday ? 'date' : undefined}
    >
      <button
        type="button"
        onClick={() => onSelect(day.date)}
        className={cn(
          'w-fit rounded px-1 font-mono text-xs tabular-nums transition-colors hover:bg-accent',
          isToday && 'bg-primary font-semibold text-primary-foreground hover:bg-primary/90',
          !isToday && (day.inMonth ? 'text-foreground' : 'text-muted-foreground'),
        )}
        aria-label={`${formatDateOnly(day.date) ?? day.date}${day.items.length ? `, ${day.items.length} dates` : ', no dates'}`}
      >
        {dayNumber === 1 ? `${dayNumber} ${month.toLocaleDateString(undefined, { month: 'short' })}` : dayNumber}
      </button>
      <div className="space-y-0.5">
        {day.items.slice(0, PER_DAY_VISIBLE).map((item) => (
          <DayEvent key={itemKey(item)} item={item} />
        ))}
        {hidden > 0 ? (
          <button
            type="button"
            onClick={() => onSelect(day.date)}
            className="w-full rounded px-1 text-left text-[11px] text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            +{hidden} more
          </button>
        ) : null}
      </div>
    </div>
  );
}

/** What is coming in the next seven days, so the month view still answers "what is next?". */
function UpcomingWeek({ items, today }: { items: TimelineItem[]; today: string }) {
  const byDate = new Map<string, TimelineItem[]>();
  for (const item of items) {
    const list = byDate.get(item.date) ?? [];
    list.push(item);
    byDate.set(item.date, list);
  }
  const days = Array.from(byDate.entries()).sort(([a], [b]) => a.localeCompare(b));
  return (
    <aside className={cn(SR_PANEL, 'p-4')} aria-label="Upcoming this week">
      <h2 className="text-sm font-semibold">Upcoming this week</h2>
      {days.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">Nothing scheduled in the next seven days.</p>
      ) : (
        <ul className="mt-3 divide-y">
          {days.map(([date, rows]) => (
            <li key={date} className="py-2.5 first:pt-0 last:pb-0">
              <p className={cn('text-xs font-medium', date === today ? 'text-primary' : 'text-muted-foreground')}>
                {date === today ? 'Today' : formatDateOnly(date) ?? date}
              </p>
              <ul className="mt-1 space-y-1.5">
                {rows.map((item) => (
                  <li key={itemKey(item)}>
                    <Link to={timelineItemPath(item)} className="block hover:no-underline">
                      <span className="flex items-center gap-1.5">
                        <RagDot rag={item.rag} />
                        <span className="min-w-0 truncate text-sm font-medium">{timelineKindLabel(item.kind)}</span>
                        <span className="shrink-0 font-mono text-[11px] font-semibold tabular-nums text-primary">
                          {item.task_key ?? item.work_item_key}
                        </span>
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                        {[item.customer, itemTitle(item)].filter(Boolean).join(' · ')}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
      <HealthLegend className="mt-4 border-t pt-3" />
    </aside>
  );
}

/** Full detail for the day the reader picked, since a cell only has room for a title. */
function DayDetail({ date, items }: { date: string; items: TimelineItem[] }) {
  return (
    <section className={cn(SR_PANEL, 'overflow-hidden')} aria-label={`Dates on ${formatDateOnly(date) ?? date}`}>
      <header className="flex items-baseline gap-2 border-b px-3 py-2">
        <h2 className="text-sm font-semibold">{formatDateOnly(date) ?? date}</h2>
        <span className="ml-auto font-mono text-xs tabular-nums text-muted-foreground">{items.length}</span>
      </header>
      {items.length === 0 ? (
        <p className="px-3 py-3 text-sm text-muted-foreground">Nothing scheduled on this day.</p>
      ) : (
        <ul className="divide-y">
          {items.map((item) => (
            <li key={itemKey(item)}>
              <Link
                to={timelineItemPath(item)}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm transition-colors hover:bg-accent/60 hover:no-underline"
              >
                <KindBadge kind={item.kind} />
                <span className="font-mono text-[11px] font-semibold tabular-nums text-primary">{item.task_key ?? item.work_item_key}</span>
                <span className="min-w-0 flex-1 truncate font-medium">{itemTitle(item)}</span>
                {item.task_id ? (
                  <span className="hidden truncate text-xs text-muted-foreground md:inline">
                    {item.work_item_key} · {item.title}
                  </span>
                ) : null}
                {item.customer ? <span className="truncate text-xs text-muted-foreground">{item.customer}</span> : null}
                <StageChip stage={item.stage} />
                {item.task_completed ? (
                  <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 dark:text-emerald-300">
                    <CheckCircle2 className="h-3 w-3" aria-hidden />
                    Done
                  </span>
                ) : null}
                <RagDot rag={item.rag} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function ServiceRequestTimeline() {
  const [searchParams, setSearchParams] = useSearchParams();
  const today = useMemo(() => startOfDay(new Date()), []);
  const todayKey = toDateOnly(today);
  const { month } = useMemo(() => parseTimelineCalendarQuery(searchParams, today), [searchParams, today]);
  const [items, setItems] = useState<TimelineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  const range = useMemo(() => monthGridRange(month), [month]);
  const monthKey = toMonthKey(month);

  useEffect(() => {
    setSelected(null);
  }, [monthKey]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchServiceRequestTimeline(range, []);
      setItems(res.items);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load the calendar'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    void load();
  }, [load]);

  function update(patch: { month?: Date }) {
    const params = new URLSearchParams(searchParams);
    const nextMonth = patch.month ?? month;
    if (toMonthKey(nextMonth) === toMonthKey(new Date(today.getFullYear(), today.getMonth(), 1))) {
      params.delete('month');
    } else {
      params.set('month', toMonthKey(nextMonth));
    }
    params.delete('kind');
    setSearchParams(params, { replace: true });
  }

  const weeks = useMemo(() => buildMonthGrid(month, items), [month, items]);
  const headings = useMemo(() => weekdayHeadings(), []);
  const inMonthCount = useMemo(
    () => weeks.flat().filter((day) => day.inMonth).reduce((total, day) => total + day.items.length, 0),
    [weeks],
  );
  const selectedItems = useMemo(
    () => (selected ? items.filter((item) => item.date === selected) : []),
    [items, selected],
  );
  const weekAhead = useMemo(() => {
    const limit = toDateOnly(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 7));
    return items
      .filter((item) => item.date >= todayKey && item.date <= limit)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [items, today, todayKey]);

  return (
    <ServiceRequestShell
      active="calendar"
      subtitle={month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
    >
      <>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              aria-label="Previous month"
              onClick={() => update({ month: addMonths(month, -1) })}
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              aria-label="Next month"
              onClick={() => update({ month: addMonths(month, 1) })}
            >
              <ChevronRight className="h-4 w-4" aria-hidden />
            </Button>
          </div>
          <h2 className="text-base font-semibold">
            {month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
          </h2>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 px-2 text-xs"
            onClick={() => update({ month: new Date(today.getFullYear(), today.getMonth(), 1) })}
          >
            Today
          </Button>
        </div>

        {error ? <ErrorAlert>{error}</ErrorAlert> : null}

        {loading ? (
          <LoadingList rows={6} />
        ) : error ? null : (
          <>
            {inMonthCount === 0 ? (
              <p className="text-sm text-muted-foreground">
                No dates this month.
              </p>
            ) : null}
            <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_19rem]">
            <div className={cn(SR_PANEL, 'overflow-hidden')}>
              <div className="grid grid-cols-7 border-b bg-muted/40">
                {headings.map((heading) => (
                  <div key={heading} className="px-1.5 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    {heading}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 [&>*:nth-child(7n)]:border-r-0 [&>*:nth-last-child(-n+7)]:border-b-0">
                {weeks.flat().map((day) => (
                  <DayCell
                    key={day.date}
                    day={day}
                    today={todayKey}
                    month={month}
                    selected={selected === day.date}
                    onSelect={(date) => setSelected((current) => (current === date ? null : date))}
                  />
                ))}
              </div>
            </div>
            <UpcomingWeek items={weekAhead} today={todayKey} />
            </div>
            {selected ? <DayDetail date={selected} items={selectedItems} /> : null}
          </>
        )}
      </>
    </ServiceRequestShell>
  );
}
